import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useWhiteLabel } from '@/hooks/useWhiteLabel';

interface WhiteLabelConfig {
  instance_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  custom_domain: string | null;
  brand_colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  features_enabled: {
    ai: boolean;
    iptv: boolean;
    vpn: boolean;
    plugins: boolean;
    transcoding: boolean;
  };
}

interface WhiteLabelContextType {
  config: WhiteLabelConfig | null;
  isLoading: boolean;
  isWhiteLabeled: boolean;
}

const defaultConfig: WhiteLabelConfig = {
  instance_name: 'Vewly',
  logo_url: null,
  favicon_url: null,
  custom_domain: null,
  brand_colors: {
    primary: 'hsl(221 83% 53%)',
    secondary: 'hsl(210 40% 96%)',
    accent: 'hsl(221 83% 53%)',
  },
  features_enabled: {
    ai: true,
    iptv: true,
    vpn: false,
    plugins: true,
    transcoding: true,
  },
};

const WhiteLabelContext = createContext<WhiteLabelContextType>({
  config: defaultConfig,
  isLoading: false,
  isWhiteLabeled: false,
});

export function WhiteLabelProvider({ children }: { children: ReactNode }) {
  const { data: whiteLabelConfig, isLoading } = useWhiteLabel();
  const [config, setConfig] = useState<WhiteLabelConfig>(defaultConfig);

  useEffect(() => {
    if (whiteLabelConfig) {
      setConfig({
        instance_name: whiteLabelConfig.instance_name || defaultConfig.instance_name,
        logo_url: whiteLabelConfig.logo_url,
        favicon_url: whiteLabelConfig.favicon_url,
        custom_domain: whiteLabelConfig.custom_domain,
        brand_colors: {
          primary: whiteLabelConfig.brand_colors?.primary || defaultConfig.brand_colors.primary,
          secondary: whiteLabelConfig.brand_colors?.secondary || defaultConfig.brand_colors.secondary,
          accent: whiteLabelConfig.brand_colors?.accent || defaultConfig.brand_colors.accent,
        },
        features_enabled: {
          ai: whiteLabelConfig.features_enabled?.ai ?? defaultConfig.features_enabled.ai,
          iptv: whiteLabelConfig.features_enabled?.iptv ?? defaultConfig.features_enabled.iptv,
          vpn: whiteLabelConfig.features_enabled?.vpn ?? defaultConfig.features_enabled.vpn,
          plugins: whiteLabelConfig.features_enabled?.plugins ?? defaultConfig.features_enabled.plugins,
          transcoding: whiteLabelConfig.features_enabled?.transcoding ?? defaultConfig.features_enabled.transcoding,
        },
      });

      // Apply brand colors to CSS variables
      document.documentElement.style.setProperty('--primary', whiteLabelConfig.brand_colors?.primary || defaultConfig.brand_colors.primary);
      document.documentElement.style.setProperty('--secondary', whiteLabelConfig.brand_colors?.secondary || defaultConfig.brand_colors.secondary);
      document.documentElement.style.setProperty('--accent', whiteLabelConfig.brand_colors?.accent || defaultConfig.brand_colors.accent);

      // Update favicon if provided
      if (whiteLabelConfig.favicon_url) {
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = whiteLabelConfig.favicon_url;
        document.getElementsByTagName('head')[0].appendChild(link);
      }

      // Update page title
      document.title = `${whiteLabelConfig.instance_name} - Home Media`;
    }
  }, [whiteLabelConfig]);

  return (
    <WhiteLabelContext.Provider
      value={{
        config,
        isLoading,
        isWhiteLabeled: !!whiteLabelConfig,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
}

export const useWhiteLabelContext = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhiteLabelContext must be used within WhiteLabelProvider');
  }
  return context;
};